/**
 * Note (Foundry: rule-element/roll-note.ts): text attached to the rolls of one or more selectors,
 * optionally only for some degrees of success. Shown in the statistic's breakdown; #19 shows it
 * with the roll.
 */
import { resolveInjectedProperties, resolveValue } from "../resolve";
import type { EngineState } from "../state";
import { DEGREE_OF_SUCCESS_STRINGS, type DegreeOfSuccessString } from "../values";
import { reduceItemName, type RuleHandler, type RuleInstance } from "./base";

interface NoteData {
  selectors: string[];
  title: string | null;
  visibility: "gm" | "owner" | null;
  outcome: DegreeOfSuccessString[];
  text: unknown;
  battleForm: boolean;
}

function parse(rule: RuleInstance): void {
  const s = rule.source;
  const raw = s.selector;
  const selectors = (Array.isArray(raw) ? raw : [raw]).filter((x): x is string => typeof x === "string" && x.length > 0);
  if (selectors.length === 0) return rule.fail("selector: must have at least 1");
  if (s.visibility !== undefined && s.visibility !== null && s.visibility !== "gm" && s.visibility !== "owner") return rule.fail(`visibility: "${String(s.visibility)}" is not gm or owner`);
  const outcome = Array.isArray(s.outcome) ? s.outcome : [];
  for (const o of outcome) if (!(DEGREE_OF_SUCCESS_STRINGS as readonly string[]).includes(String(o))) return rule.fail(`outcome: "${String(o)}" is not a degree of success`);
  if (typeof s.text !== "string" || !s.text) return rule.fail("text: must be a non-empty string");
  const data: NoteData = {
    selectors,
    title: typeof s.title === "string" && s.title ? s.title : null,
    visibility: (s.visibility as "gm" | "owner" | undefined) ?? null,
    outcome: outcome as DegreeOfSuccessString[],
    text: s.text,
    battleForm: s.battleForm === true,
  };
  rule.data = data as unknown as Record<string, unknown>;
}

function beforePrepareData(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  const data = rule.data as unknown as NoteData;
  const predicate = resolveInjectedProperties(rule, state, structuredClone(rule.predicate), { warn: false });
  if (data.battleForm && !predicate.includes("battle-form")) predicate.push("battle-form");
  for (const raw of data.selectors) {
    const selector = resolveInjectedProperties(rule, state, raw);
    if (selector === "null" || rule.ignored) continue;
    const rawTitle = data.title ? resolveInjectedProperties(rule, state, data.title).trim() : null;
    const title = rawTitle ? reduceItemName(state.catalogue?.localize(rawTitle) ?? rawTitle) : null;
    const resolvedText = resolveInjectedProperties(rule, state, String(resolveValue(rule, state, data.text, "", { evaluate: false }))).trim();
    const text = state.catalogue?.localize(resolvedText) ?? resolvedText;
    if (!text) return rule.fail("text field resolved empty");
    (state.synthetics.rollNotes[selector] ??= []).push({
      selector,
      title,
      text,
      predicate,
      parentOptions: rule.item.getRollOptions("parent"),
      outcome: [...data.outcome],
      visibility: data.visibility,
      source: rule.item.uuid,
      sourceName: rule.item.name,
    });
  }
}

export const NoteHandler: RuleHandler = { key: "Note", parse, beforePrepareData };
